import React, { useState, useMemo } from 'react';
import tinycolor from 'tinycolor2';
import { Box, Typography, TextField, Grid, Paper, Stack, Tooltip, useTheme, alpha } from '@mui/material';
import { motion } from 'framer-motion';
import CheckIcon from '@mui/icons-material/Check';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';
import './ColorConverter.css';

const ColorSlider = ({ type, value, onChange, color }) => {
  const hsl = color.toHsl();
  const sliderProps = { min: 0, max: type === 'hue' ? 360 : 100, step: 1 };
  let sliderClass = 'color-slider';
  let style = { '--slider-color': color.toHexString() };

  if (type === 'hue') sliderClass += ' hue-slider';
  else if (type === 'saturation') {
    sliderClass += ' saturation-slider';
    style = { '--slider-color': `hsl(${hsl.h}, 100%, 50%)` };
  } else if (type === 'lightness') {
    sliderClass += ' lightness-slider';
    style = { '--slider-color': `hsl(${hsl.h}, 100%, 50%)` };
  }

  if (type === 'alpha') {
    return (
      <Box>
        <Typography variant="caption" sx={{ textTransform: 'capitalize', fontWeight: 'medium' }}>Alpha</Typography>
        <Box className="alpha-slider">
          <div className="alpha-slider-gradient" style={style} />
          <input type="range" min="0" max="100" step="1" value={value} onChange={(e) => onChange(parseFloat(e.target.value))} />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="caption" sx={{ textTransform: 'capitalize', fontWeight: 'medium' }}>{type}</Typography>
      <input type="range" className={sliderClass} style={style} {...sliderProps} value={value} onChange={(e) => onChange(parseFloat(e.target.value))} />
    </Box>
  );
};

const ColorSwatch = ({ colorHex }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(colorHex);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Tooltip title={`Click to copy ${colorHex}`}>
      <motion.div whileHover={{ scale: 1.1, zIndex: 1 }}>
        <Paper
          onClick={handleCopy}
          sx={{
            width: { xs: 45, sm: 60 },
            height: { xs: 45, sm: 60 },
            bgcolor: colorHex,
            borderRadius: 2,
            border: '1px solid rgba(0,0,0,0.1)',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {copied && <CheckIcon sx={{ color: tinycolor(colorHex).isDark() ? 'white' : 'black' }} />}
        </Paper>
      </motion.div>
    </Tooltip>
  );
};

const ColorPalette = ({ title, colors }) => (
  <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>{title}</Typography>
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-start">
      {colors.map((c, i) => (
        <ColorSwatch key={i} colorHex={c} />
      ))}
    </Stack>
  </Box>
);

function ColorConverter() {
  const theme = useTheme();
  const [color, setColor] = useState(tinycolor('#673ab7'));

  const handleColorChange = (newColor) => {
    const tc = tinycolor(newColor);
    if (tc.isValid()) setColor(tc);
  };

  const handleHslChange = (type, value) => {
    const hsl = color.toHsl();
    if (type === 'h') hsl.h = value;
    if (type === 's') hsl.s = value / 100;
    if (type === 'l') hsl.l = value / 100;
    if (type === 'a') hsl.a = value / 100;
    setColor(tinycolor(hsl));
  };

  const colorFormats = useMemo(() => ({
    hex: color.toHexString(),
    rgba: color.toRgbString(),
    hsla: color.toHslString(),
  }), [color]);

  const palettes = useMemo(() => ({
    shades: Array.from({ length: 7 }, (_, i) => color.clone().darken(i * 6).toHexString()),
    tints: Array.from({ length: 7 }, (_, i) => color.clone().lighten(i * 8).toHexString()),
    harmonies: {
      complementary: [color.toHexString(), color.complement().toHexString()],
      triadic: color.triad().map(c => c.toHexString()),
      analogous: color.analogous().map(c => c.toHexString()),
    },
  }), [color]);

  return (
    <>
      <SEO
        title="Color Converter & Palette Generator | UI Studio | UmbrellaCo"
        description="Convert colors between HEX, RGB, HSL, and more. Generate complementary, triadic, and analogous palettes instantly for your web design projects."
        keywords="color converter, hex to rgb, hsl color picker, color palette generator, ui design tools"
        url="/color-image-tools/color-converter"
        jsonLd={createSoftwareAppSchema(
          'Color Converter Studio',
          'Convert color formats and generate custom design palettes instantly.',
          'https://umbrellaco.in/color-image-tools/color-converter',
          ['Color Conversion', 'Design Palettes', 'UI Tools']
        )}
      />
      <ToolPageContainer
        title="Color Studio"
        description="Professional color translation and palette generation."
        disablePadding
      >
      <Box sx={{ p: 3, flexGrow: 1, overflow: 'auto', borderRadius: 2, transition: 'background-color 0.5s ease', bgcolor: color.clone().setAlpha(0.05).toRgbString() }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'stretch' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper sx={{ p: 3, borderRadius: 2, height: '100%', border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>Converter & Sliders</Typography>
            <Stack spacing={2}>
              <ColorSlider type="hue" value={color.toHsl().h} onChange={(v) => handleHslChange('h', v)} color={color} />
              <ColorSlider type="saturation" value={color.toHsl().s * 100} onChange={(v) => handleHslChange('s', v)} color={color} />
              <ColorSlider type="lightness" value={color.toHsl().l * 100} onChange={(v) => handleHslChange('l', v)} color={color} />
              <ColorSlider type="alpha" value={color.getAlpha() * 100} onChange={(v) => handleHslChange('a', v)} color={color} />
            </Stack>
            <Grid container spacing={2} sx={{ mt: 3 }}>
              <Grid item xs={12} sm={6}><TextField size="small" label="HEX" value={colorFormats.hex} onChange={(e) => handleColorChange(e.target.value)} fullWidth /></Grid>
              <Grid item xs={12} sm={6}><TextField size="small" label="RGBA" value={colorFormats.rgba} onChange={(e) => handleColorChange(e.target.value)} fullWidth /></Grid>
              <Grid item xs={12}><TextField size="small" label="HSLA" value={colorFormats.hsla} onChange={(e) => handleColorChange(e.target.value)} fullWidth /></Grid>
            </Grid>
          </Paper>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper sx={{ p: 3, borderRadius: 2, height: '100%', border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>Generated Palettes</Typography>
            <Stack spacing={3}>
              <ColorPalette title="Shades" colors={palettes.shades} />
              <ColorPalette title="Tints" colors={palettes.tints} />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>Harmonies</Typography>
                <Stack spacing={2}>
                  <ColorPalette title="Complementary" colors={palettes.harmonies.complementary} />
                  <ColorPalette title="Triadic" colors={palettes.harmonies.triadic} />
                  <ColorPalette title="Analogous" colors={palettes.harmonies.analogous} />
                </Stack>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Box>
      </Box>
      </ToolPageContainer>
    </>
  );
}

export default ColorConverter;